import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import type { TDimensionFormState, TDimensionType } from "../../lib/dimensionForm";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { OptionsTagInput } from "./_OptionsTagInput";
import { RadioDot } from "./_RadioDot";
import { useTranslation } from "react-i18next";

const DIMENSION_TYPE_OPTIONS = [
  { value: "dropdown", titleKey: "dimensions.types.dropdown", descriptionKey: "dimensions.types.dropdownDescription" },
  { value: "text", titleKey: "dimensions.types.text", descriptionKey: "dimensions.types.textDescription" }
] as const satisfies readonly { value: TDimensionType; titleKey: string; descriptionKey: string }[];

type TProps = {
  mode: "add" | "edit";
  form: TDimensionFormState;
  isPending: boolean;
  canSave: boolean;
  onChange: (next: TDimensionFormState) => void;
  onCancel: () => void;
  onSave: () => void;
};

export function DimensionFormView(props: TProps) {
  const { mode, form, isPending, canSave, onChange, onCancel, onSave } = props;
  const { t } = useTranslation(["projects", "common"]);

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-5">
        <div className="flex flex-col gap-2">
          <FigmaTypography component="label" variant="label-2" className="text-zinc-900">
            {t("projects:dimensions.nameLabel")}
          </FigmaTypography>
          <input
            id="dim-modal-name"
            type="text"
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            placeholder={t("projects:dimensions.namePlaceholder")}
            disabled={isPending}
            className="h-8 w-full rounded-lg border border-[#ececee] bg-white px-3 text-[14px] text-zinc-900 placeholder:text-[#a1a1aa] focus:border-zinc-400 focus:outline-none focus:ring-0 disabled:opacity-50"
          />
        </div>

        <div className="flex flex-col gap-2">
          <FigmaTypography component="label" variant="label-2" className="text-zinc-900">
            {t("projects:dimensions.typeLabel")}
          </FigmaTypography>
          <div className="grid grid-cols-2 gap-2">
            {DIMENSION_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="flex items-center gap-2 rounded-lg border border-[#ececee] bg-white p-3 text-left transition-colors hover:border-zinc-300 disabled:opacity-50"
                onClick={() => onChange({ ...form, type: opt.value })}
                disabled={isPending}
              >
                <RadioDot selected={form.type === opt.value} />
                <div className="flex flex-1 flex-col gap-1">
                  <FigmaTypography component="span" variant="label-2" className="text-zinc-900">
                    {t(opt.titleKey)}
                  </FigmaTypography>
                  <FigmaTypography component="span" variant="body-3" className="text-zinc-500">
                    {t(opt.descriptionKey)}
                  </FigmaTypography>
                </div>
              </button>
            ))}
          </div>
        </div>

        {form.type === "dropdown" && (
          <div className="flex flex-col gap-2">
            <FigmaTypography component="label" variant="label-2" className="text-zinc-900">
              {t("projects:dimensions.optionsLabel")}
            </FigmaTypography>
            <OptionsTagInput options={form.options} onChange={(next) => onChange({ ...form, options: next })} />
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-1 border-t border-[#f4f4f5] bg-white p-5">
        <AppButton appearance="outlined" color="zinc150" onClick={onCancel} disabled={isPending}>
          {t("common:cancel")}
        </AppButton>
        <AppButton
          onClick={onSave}
          disabled={!canSave || isPending}
          isLoading={isPending}
          className={cn(
            "shadow-200",
            !canSave && !isPending && "!bg-innoveraZinc-100 !text-innoveraZinc-400 shadow-none hover:!bg-innoveraZinc-100"
          )}
        >
          {mode === "add" ? t("projects:dimensions.create") : t("projects:dimensions.save")}
        </AppButton>
      </div>
    </>
  );
}
